import Select, {
  Props as SelectProps,
  SingleValue,
  MultiValue,
  components,
} from "react-select";
import { FaChevronDown } from "react-icons/fa";
import { Control, Controller, FieldValues, Path } from "react-hook-form";

interface Option {
  value: string | number;
  label: string;
}

interface SelectFieldProps<T extends FieldValues> {
  label: string;
  name: Path<T>;
  control: Control<T>;
  options: Option[];
  isMulti?: boolean;
  placeholder?: string;
  error?: string;
  required?: boolean;
  className?: string;
  menuPlacement?: "auto" | "top" | "bottom";
  rules?: Record<string, any>;
  isDisabled?: boolean;
}

const customStyles = {
  control: (base: any, state: any) => ({
    ...base,
    border: state.isFocused ? "none" : "none",
    boxShadow: state.isFocused ? "none" : "none",
    borderRadius: "10px",
    padding: "4px",
    transition: "border-color 0.2s ease-in-out",
    height: "60px",
  }),
  placeholder: (base: any) => ({
    ...base,
    color: "#98A2B3",
    fontSize: "14px",
  }),
  singleValue: (base: any) => ({
    ...base,
    color: "#000",
    fontWeight: "400",
  }),
  dropdownIndicator: (base: any) => ({
    ...base,
    color: "#98A2B3",
    fontSize: "20px",
  }),
  menu: (base: any) => ({
    ...base,
    borderRadius: "8px",
    boxShadow: " 0px 0px 15px 0px #00000012",
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#D1A960"
      : state.isFocused
      ? "#D1A960"
      : "white",
    color: state.isSelected ? "white" : "#111827",
    padding: "10px 12px",
    cursor: "pointer",
    textAlign: "left",
    ":active": {
      backgroundColor: "#D1A960", // Change this to your desired active color
    },
  }),
};

const DropdownIndicator = (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      <FaChevronDown style={{ color: "#98A2B3" }} className="text-base" />{" "}
    </components.DropdownIndicator>
  );
};

function IncomeSelectField<T extends FieldValues>({
  label,
  name,
  control,
  options,
  isMulti = false,
  placeholder = "Select...",
  error,
  required = false,
  className = "",
  menuPlacement = "auto",
  rules = {},
  isDisabled = false,
}: SelectFieldProps<T>) {
  const formattedPlaceholder = placeholder
    ? placeholder.charAt(0).toUpperCase() + placeholder.slice(1).toLowerCase()
    : "";
  return (
    <div
      className={`text-left react-select-wrapper  income-select-dropdown ${className}`}
    >
      <Controller
        name={name}
        control={control}
        rules={{
          ...rules,
          required: required ? `${label} is required` : false,
        }}
        render={({ field }) => (
          <Select
            {...field}
            styles={customStyles}
            options={options}
            isMulti={isMulti}
            placeholder={formattedPlaceholder}
            menuPlacement={menuPlacement}
            onChange={(selectedOption) => {
              if (isMulti) {
                const multiValue = selectedOption as MultiValue<Option>;
                field.onChange(multiValue.map((option) => option.value));
              } else {
                const singleValue = selectedOption as SingleValue<Option>;
                field.onChange(singleValue !== null ? singleValue.value : null);
              }
            }}
            value={
              isMulti
                ? options.filter((option) =>
                    Array.isArray(field.value)
                      ? field.value.includes(option.value)
                      : false
                  )
                : options.find((option) => option.value === field.value) || null
            }
            classNamePrefix="react_select selectfield"
            components={{ DropdownIndicator }}
            isDisabled={isDisabled}
          />
        )}
      />
    </div>
  );
}

export default IncomeSelectField;
